import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {SavedProperty} from "../../../model/savedProperty";
import {ActivatedRoute, Router} from "@angular/router";
import {SavedPropertyService} from "../../../service/saved-property.service";
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {DropzoneComponent} from "../../dropzone/dropzone.component";
import {SavedLandregisterfolio} from "../../../model/savedLandregisterfolio";
import {SavedFlur} from "../../../model/savedFlur";
import {SavedFlurstueck} from "../../../model/savedFlurstueck";
import {CustomValidators} from "../../../CustomValidators";
import {PlatformProfile} from "../../../model/platformprofile";
import {Mandate} from "../../../model/mandate";
import {SavedBillingAddress} from "../../../model/savedBillingAddress";
import {SavedBeneficiariesAddress} from "../../../model/savedBeneficiariesAddress";
import {SavedAuthority} from "../../../model/savedAuthority";
import {IDeactivateComponent} from "../../../guards/deactivate.guard";
import {Observable, Subscription} from "rxjs";
import {LoginIdService} from "../../../service/login-id.service";
import {startWith} from "rxjs/operators";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import {Country} from "../../../model/country";
import {CountryService} from "../../../service/country.service";
import StaticData from "../../../utils/static-data";

@Component({
  selector: 'app-saved-property-details',
  templateUrl: './saved-property-details.component.html',
  styleUrls: ['./saved-property-details.component.css']
})
export class SavedPropertyDetailsComponent implements OnInit, IDeactivateComponent {
  protected readonly faArrowLeft = faArrowLeft;

  savedProperty: SavedProperty;
  savedPropertyId: string;
  savedPropertyForm: FormGroup
  errorMsg: string;
  mandateErrorMsg: string;
  searchText: string;
  foundMandate: boolean;
  showMandateModal: boolean;
  inputFocus:boolean;
  platformProfile: PlatformProfile;
  mandates: Array<Mandate> = new Array<Mandate>();
  selectedMandate: Mandate;
  shouldBillingAddressEqualBeneficiaryAddress: boolean = false;
  canExitWithoutAsking: boolean = false;
  companyLoginSubscription: Subscription;
  countries: Array<Country> = [];
  @ViewChild('authorityDropzone') authorityDropzoneComponent: DropzoneComponent;
  @ViewChild('mandateImageDropzone') mandateImageDropzoneComponent: DropzoneComponent;
  @ViewChild('mandateName') mandateNameInput;

    constructor(private route: ActivatedRoute, private savedPropertiesService: SavedPropertyService, private formBuilder: FormBuilder, private router: Router, private loginIdService: LoginIdService, private countryService: CountryService) {
    }

  async ngOnInit(): Promise<void> {
      await this.loginIdService.loginServiceInitialized$.toPromise();

      this.countries = this.countryService.getCountries();

      this.initPlatformProfile();

      this.companyLoginSubscription = this.loginIdService.loginIdChanged$.pipe(startWith(true)).subscribe(() => {
          this.initPlatformProfile();
      })

      this.canExitWithoutAsking = false;

      this.savedPropertyId = this.route.snapshot.params.savedPropertyId

      if (this.savedProperty == null && this.savedPropertyId !== 'new') {
          this.initExistingSavedProperty();
      } else {
          if (this.savedPropertyId === 'new') {
              this.initNewSavedProperty();
          }
          this.initFormGroup();
      }
  }

  /**
   * Initializes this.savedProperty with an already existing saved property from the api
   */
  private initExistingSavedProperty(): void{
    this.savedPropertiesService.getSavedPropertyById(this.savedPropertyId,this.loginIdService.getCurrentCustomHeadersWithoutSpinner()).subscribe(res => {
      this.savedProperty = res;
      this.initFormGroup();
    })
  }

  /**
   * Initializes a new SavedProperty Object with empty SavedBillingAddress, SavedBeneficiariesAddress and SavedAuthority
   */
  private initNewSavedProperty(): void{
    this.savedProperty = new SavedProperty();
    this.savedProperty.billingAddress = new SavedBillingAddress();
    this.savedProperty.beneficiariesAddress = new SavedBeneficiariesAddress();
    this.savedProperty.authorities = new Array<SavedAuthority>();
  }

  /**
   * Initializes the platform profile and the corresponding mandates of a platform profile
   */
  private initPlatformProfile(): void{
    this.platformProfile = JSON.parse(sessionStorage.getItem("platform"));
    if(this.platformProfile){
      this.mandates = this.platformProfile.mandate;
    }
  }

  initFormGroup(){

    this.shouldBillingAddressEqualBeneficiaryAddress = this.isBillingAddressEqualsBeneficiaryAddress()

    if(this.savedProperty.mandateId){
      this.selectedMandate = this.platformProfile.mandate.find(m => m.id === this.savedProperty.mandateId);
    }

    this.searchText = this.selectedMandate ? this.selectedMandate.name : "";

    this.savedPropertyForm = this.buildFormGroup();

    this.initSavedPropertyLandregisterForms();
  }

  /**
   * Build the form group for a saved property with all necessary sub form groups
   * @return Created form group for the saved property
   */
  private buildFormGroup(): FormGroup {
    return this.formBuilder.group({

      id: new FormControl(this.savedProperty.id),
      platformProfileId: new FormControl(this.savedProperty.platformProfileId),

      mandateId: new FormControl(this.savedProperty.mandateId),
      ownId: new FormControl(this.savedProperty.ownId),
      name: new FormControl(this.savedProperty.name),
      contactMail: new FormControl(this.savedProperty.contactMail),
      street: new FormControl(this.savedProperty.street, Validators.required),
      house_number: new FormControl(this.savedProperty.house_number),
      zip: new FormControl(this.savedProperty.zip, Validators.compose([
        Validators.required,
        CustomValidators.patternValidator(/^[0-9]{5}$/gm, {isPlzValid: true})
      ])),
      town: new FormControl(this.savedProperty.town, Validators.required),
      landregisterfolios: this.formBuilder.array(this.createFolios()),
      authorities: this.formBuilder.array(this.savedProperty.authorities),

      billingAddress: new FormGroup({
        id: new FormControl(this.savedProperty.billingAddress ? this.savedProperty.billingAddress.id : ""),
        firma: new FormControl(this.savedProperty.billingAddress ? this.savedProperty.billingAddress.firma : null, Validators.required),
        street: new FormControl(this.savedProperty.billingAddress ? this.savedProperty.billingAddress.street : null, Validators.required),
        zip: new FormControl(this.savedProperty.billingAddress ? this.savedProperty.billingAddress.zip : null, Validators.compose([
          Validators.required
        ])),
        town: new FormControl(this.savedProperty.billingAddress ? this.savedProperty.billingAddress.town : null, Validators.required),
        country: new FormControl(this.savedProperty.billingAddress && this.savedProperty.billingAddress.country ? this.savedProperty.billingAddress.country : StaticData.DEFAULT_COUNTRY, Validators.required),
      }),

      beneficiariesAddress: new FormGroup({
        id: new FormControl(this.savedProperty.beneficiariesAddress ? this.savedProperty.beneficiariesAddress.id : ""),
        beneficiary: new FormControl(this.savedProperty.beneficiariesAddress ? this.savedProperty.beneficiariesAddress.beneficiary : null, Validators.required),
        street: new FormControl(this.savedProperty.beneficiariesAddress ? this.savedProperty.beneficiariesAddress.street : null, Validators.required),
        zip: new FormControl(this.savedProperty.beneficiariesAddress ? this.savedProperty.beneficiariesAddress.zip : null, Validators.compose([
          Validators.required
        ])),
        town: new FormControl(this.savedProperty.beneficiariesAddress ? this.savedProperty.beneficiariesAddress.town : null, Validators.required),
        country: new FormControl(this.savedProperty.beneficiariesAddress && this.savedProperty.beneficiariesAddress.country ? this.savedProperty.beneficiariesAddress.country : StaticData.DEFAULT_COUNTRY, Validators.required)
      }),

    });
  }

  /**
   * Initializes the landregister folio sub form array of the saved property form by setting validators for the form array itself and its sub forms (flure, flstk)
   * @private
   */
  private initSavedPropertyLandregisterForms(): void {
    let folioFormArray = (this.savedPropertyForm.get("landregisterfolios")) as FormArray

    for (let folio of folioFormArray.controls) {
      folio.get("gemarkung").setValidators(Validators.required);
      folio.get("gemarkung").updateValueAndValidity();
      folio.get("number").setValidators(Validators.required);
      folio.get("number").updateValueAndValidity();

      for (let flure of folio.get('flure')['controls']) {
        for (let flstk of flure.get('flurstuecke')['controls']) {
          flstk.get('number').setValidators(Validators.required);
          flstk.get('number').updateValueAndValidity();
        }
      }
    }

    if (!this.savedProperty.id) {
      this.addFolio()
    }
  }

  /**
   * Checks if a specific Abstract control has an error by checking if its invalid and was touched
   *
   * @param control Abstract control that should be checked
   * @return true if the AbstractControl is invalid and was touched else false
   */
  hasError(control: AbstractControl): boolean {
    return (control.invalid && (control.touched));
  }

  /**
   * Deletes a specific 'landregisterfolio' from the form array of the saved property form
   *
   * @param folioIndex Index of the 'Landregisterfolio' that should be removed
   */
  deleteFolio(folioIndex: number) {
    const control = <FormArray>this.savedPropertyForm.get('landregisterfolios');
    control.removeAt(folioIndex);
  }

  /**
   * Deletes a specific 'flur' from the form array of 'landregisterfolio' of the saved property form
   *
   * @param folioIndex Index of the 'landregisterfolio' for which the flur should be removed
   * @param flurIndex Index of the 'flur' that should be removed
   */
  deleteFlur(folioIndex: number, flurIndex: number) {

    const control = <FormArray>this.savedPropertyForm.get(['landregisterfolios', folioIndex, 'flure']);
    control.removeAt(flurIndex);
  }

  /**
   * Deletes a specific 'flurstueck' from the form array of 'flur' which is an form array of 'landregisterfolio' of the saved property form
   *
   * @param folioIndex Index of the 'landregisterfolio' for which the flurstueck should be removed
   * @param flurIndex Index of the 'flur' for which the flurstueck should be removed
   * @param flurstueckIndex Index of the 'flurstueck' that should be removed
   */
  deleteFlurstueck(folioIndex: number, flurIndex: number, flurstueckIndex: number) {
    const control = <FormArray>this.savedPropertyForm.get(['landregisterfolios', folioIndex, 'flure', flurIndex, 'flurstuecke']);
    control.removeAt(flurstueckIndex);
  }

  /**
   * Adds a 'flurstueck' to a form array of 'flur' which is part of 'landregisterfolio' which is part of the saved property form
   *
   * @param folioIndex Index of the 'landregisterfolio' for which the flur should be added
   * @param flurIndex Index of the 'flur' to which the flurstueck should be added
   */
  addFlurstueck(folioIndex: number, flurIndex: number) {
    const group = <FormGroup>this.savedPropertyForm.get('landregisterfolios');
    let controlFlure = <FormArray>group.controls[folioIndex].get('flure');
    let controlFlurstueck = <FormArray>controlFlure.controls[flurIndex].get('flurstuecke');
    controlFlurstueck.push(this.initFlurstueck(new SavedFlurstueck()));
  }

  /**
   * Adds a 'flur' to a form array of 'landregisterfolio' which is part of the saved property form
   *
   * @param folioIndex Index of the 'landregisterfolio' to which the flur should be added
   */
  addFlur(folioIndex: number) {
    const group = <FormGroup>this.savedPropertyForm.get('landregisterfolios');
    let control = <FormArray>group.controls[folioIndex].get('flure');
    control.push(this.initFlur(new SavedFlur()));
  }

  /**
   * Adds a 'landregisterfolio' to the saved property form
   */
  addFolio() {
    const control = <FormArray>this.savedPropertyForm.get('landregisterfolios');
    control.push(this.initFolio(new SavedLandregisterfolio()));
  }

  /**
   * Get all AbstractControl of the form array 'landregisterfolios' which is part of the saved property form
   *
   * @param form Form from which the controls should be gotten
   * @return All AbstractControls ob the form array 'landregisterfolios'
   */
  getFolios(form) {
    return form.controls.landregisterfolios.controls;
  }

  /**
   * Get all AbstractControl of the form array 'flure' which is part of 'landregisterfolios' in saved property form
   *
   * @param form Form from which the controls should be gotten
   * @return All AbstractControl of the form array 'flure'
   */
  getFlure(form) {
    return form.controls.flure.controls;
  }

  /**
   * Get all AbstractControls of the form array 'flurstuecke' which is part of 'flure' which is part of 'landregisterfolios' in saved property form
   *
   * @param form Form from which the control should be gotten
   * @return All AbstactControls of the form array 'flurstuecke'
   */
  getFlurstuecke(form) {
    return form.controls.flurstuecke.controls;
  }

  /**
   * Get all authorities from a specific form
   *
   * @param form Form from which the authorities should be gotten
   * @return All Authorities from the form that was given
   */
  getAuthorities(form){
    return form.controls.authorities.value;
  }

  /**
   * Creates an AbstractControl for each 'landregisterfolio' of the form array from the saved property form. Each AbstactControl is initialized with the objects data
   *
   * @return Array of all created AbstractControls that where created
   */
  createFolios(): AbstractControl[] {
    let result: AbstractControl[] = [];
    let that = this;
    this.savedProperty.landregisterfolios.forEach(function (folio) {
      result.push(that.initFolio(folio));
    });
    return result;
  }

  /**
   * Initializes a FormGroup for a landregisterfolio based on a given saved lanregister folio
   *
   * @param folio SavedLandregisterfolio of which the data should be put into a form group
   * @return Created form group for a specific SavedLandregisterfolio
   */
  initFolio(folio: SavedLandregisterfolio) {
    return new FormGroup({
      id: new FormControl(folio.id),
      number: new FormControl(folio.number, Validators.required),
      gemarkung: new FormControl(folio.gemarkung, Validators.required),
      flure: this.formBuilder.array(this.createFlur(folio))
    });
  }

  /**
   * Creates an array of form groups for each flur of a saved landregister folio. Each formgroup is initialized with the objects data
   *
   * @param folio SavedLandregisterfolio for which formGroups of each flur should be created an initialized
   * @return Array of all created form groups for each flur of a saved landregister folio
   */
  createFlur(folio: SavedLandregisterfolio): Array<FormGroup> {
    let result: Array<FormGroup> = [];
    let that = this;
    folio.flure.forEach(function (flur) {
      result.push(that.initFlur(flur));
    });
    return result;
  }

  /**
   * Initializes a FormGroup for a flur based on a given SavedFlur
   *
   * @param flur SavedFlur of which the data should be put into a form group
   * @return Created form group for a specific SavedFlur
   */
  initFlur(flur: SavedFlur) {
    return this.formBuilder.group({
      id: new FormControl(flur.id),
      number: new FormControl(flur.number),
      flurstuecke: this.formBuilder.array(this.createFlurstuecke(flur))
    });
  }

  /**
   * Creates an array of form groups for each flurstueck of a SavedFlur. Each form group is initialized with the objects data
   *
   * @param flur SavedFlur for which formGroups of each flurstueck should be created and initialized
   * @return Array of all created form groups for each flurstueck of a SavedFlur
   */
  createFlurstuecke(flur: SavedFlur): Array<FormGroup> {
    let result: Array<FormGroup> = [];
    let that = this;
    flur.flurstuecke.forEach(function (fs) {
      result.push(that.initFlurstueck(fs));
    });

    return result;
  }

  /**
   * Initializes a FormGroup for a flurstueck based on a given SavedFlurstueck
   *
   * @param fs SavedFlurstueck of which the data should be put into a form group
   * @return Created form group for a specific SavedFlurstueck
  */
  initFlurstueck(fs: SavedFlurstueck) {
    return this.formBuilder.group({
      id: new FormControl(fs.id),
      number: new FormControl(fs.number , Validators.required),
      geometry: new FormControl(fs.geometry)
    });
  }

  /**
   * Shows an authority of a saved property in a new window that opens in the middle of the screen
   *
   * @param index Index of the authority of a saved property that should be opened
   */
  showAuthority(index){
    let s3Key = this.savedProperty.authorities[index].s3Key
    this.savedPropertiesService.getAuthorityByIdAndS3Key(this.savedPropertyId,s3Key, this.loginIdService.getCurrentHeaders()).subscribe(res => {
      const blob = new Blob([res], {type: "application/pdf"});
      const blobUrl = URL.createObjectURL(blob);
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      const windowWidth = Math.floor(screenWidth * 0.5);
      const windowHeight = Math.floor(screenHeight * 0.8);
      const left = (screenWidth - windowWidth) / 2;
      const top = (screenHeight - windowHeight) / 2;
      const params = "left=" + left + ",top=" + top + ",width=" + windowWidth + ",height=" + windowHeight;
      window.open(blobUrl,"Vollmacht " + index + ".pdf",params);
    }, error => {
      console.log(error)
    })
  }

  /**
   * Removes an authority of the saved property form by a specific index
   *
   * @param index Index of the saved authority that should be removed from the saved property form
   */
  removeSavedAuthority(index){
    (<FormArray>this.savedPropertyForm.controls.authorities).removeAt(index);
  }

  /**
   * Updates a specific saved property specified by its id. This includes updating the actual object and its authorities.
   * After the update the platform profile is being updated to show the updated data.
   *
   * @param id Id of the saved property that should be updated
   */
  updateSavedProperty(id){
    if(!this.savedPropertyForm.valid){
      this.errorMsg = "Bitte geben Sie alle relevanten Objektdaten an."
      this.savedPropertyForm.markAllAsTouched()
    }
    else{
      let savedAuthoritiesFormArray = (<FormArray>this.savedPropertyForm.controls.authorities);
      if(savedAuthoritiesFormArray.length < 1 && this.authorityDropzoneComponent.files.length < 1){
        this.errorMsg = "Jedes Objekt benötigt mindestens eine passende Vollmacht."
      }
      else{
        this.savedPropertiesService.updateSavedPropertyById(id, this.savedPropertyForm.value, this.authorityDropzoneComponent.files, this.loginIdService.getCurrentHeaders()).subscribe(res => {
            const updatedPropertyIndex = this.platformProfile.properties.findIndex(savedProperty => savedProperty.id === res.id);
            if(updatedPropertyIndex !== -1){
                this.platformProfile.properties[updatedPropertyIndex] = res;
                this.canExitWithoutAsking = true;
                this.updateUserPlatformMapping(this.loginIdService.getCurrentHeaders().get("company"), this.platformProfile);
                this.router.navigate(['/saved-properties']);
            }
        },error => {
          console.log(error)
        })
      }
    }
  }

  /**
   * Adds a saved property to a platform profile. This includes adding the actual object and its authorities.
   * After the saved property is added the platform profile is being updated to show the updated data.
   */
  addSavedProperty(){
    this.updateBeneficiaryAddress(this.shouldBillingAddressEqualBeneficiaryAddress, true);
    if(!this.savedPropertyForm.valid){
      this.errorMsg = "Bitte geben Sie alle relevanten Objektdaten an."
      this.savedPropertyForm.markAllAsTouched()
    }
    else{
      let savedAuthoritiesFormArray = (<FormArray>this.savedPropertyForm.controls.authorities);
      if(savedAuthoritiesFormArray.length < 1 && this.authorityDropzoneComponent.files.length < 1){
        this.errorMsg = "Jedes Objekt benötigt mindestens eine passende Vollmacht."
      }
      else{
        this.savedPropertyForm.controls["platformProfileId"].setValue(this.platformProfile.id);
        this.savedPropertiesService.addSavedProperty(this.savedPropertyForm.value, this.authorityDropzoneComponent.files, this.loginIdService.getCurrentHeaders()).subscribe(res => {
            this.platformProfile.properties.push(res);
            this.canExitWithoutAsking = true;
            this.updateUserPlatformMapping(this.loginIdService.getCurrentHeaders().get("company"), this.platformProfile);
            this.router.navigate(['/saved-properties']);
        },error => {
          console.log(error)
        })
      }
    }
  }

    /**
     * Updates the user platform mapping and the platform in the session storage
     *
     * @param userId Id of an user for which the user platform mapping should be updated
     * @param platformProfile Updated PlatformProfile
     */
  private updateUserPlatformMapping(userId: string, platformProfile: PlatformProfile): void{
      const userPlatformMappingFromSessionStorage = sessionStorage.getItem("user_platform_mapping");
      sessionStorage.setItem("platform",JSON.stringify(this.platformProfile));
      if(userPlatformMappingFromSessionStorage){
          const userPlatformMapping: Map<string, PlatformProfile> = new Map(JSON.parse(userPlatformMappingFromSessionStorage));
          userPlatformMapping.set(userId,platformProfile);
          this.loginIdService.updatePlatformUserMapping(userPlatformMapping);
          sessionStorage.setItem("user_platform_mapping", JSON.stringify(Array.from(userPlatformMapping.entries())))
      }
  }

  /**
   * Updates the beneficiaries address form which is part of the saved property form.
   * If shouldBillingAddressEqualBeneficiaryAddress is true the values of the beneficiary address are getting set so the same values as the billing address
   * else the form is initialized with empty values.
   *
   * @param shouldBillingAddressEqualBeneficiaryAddress Determines if the beneficiaries address should have the same values as the billing address
   * @param addingSavedProperty Determines if this function is triggered by adding a saved Property.
   *                            If this is the case the beneficiaries form should not be reset when shouldBillingAddressEqualBeneficiaryAddress is false
   */
  private updateBeneficiaryAddress(shouldBillingAddressEqualBeneficiaryAddress: boolean, addingSavedProperty: boolean = false): void{
      const billingAddress = this.savedPropertyForm.controls["billingAddress"];
      const beneficiariesAddress = this.savedPropertyForm.controls["beneficiariesAddress"];

      if(!shouldBillingAddressEqualBeneficiaryAddress && !addingSavedProperty){
        beneficiariesAddress.patchValue({
          beneficiary: '',
          street: '',
          zip:'',
          town: '',
          country: StaticData.DEFAULT_COUNTRY,
        })
      }
      else if(shouldBillingAddressEqualBeneficiaryAddress){
        beneficiariesAddress.patchValue({
          beneficiary: billingAddress.value.firma,
          street: billingAddress.value.street,
          zip: billingAddress.value.zip,
          town: billingAddress.value.town,
          country: billingAddress.value.country
        })
      }
      billingAddress.updateValueAndValidity();
      beneficiariesAddress.updateValueAndValidity();
  }

  /**
   * Toggles if the beneficiaries address should be equal to the billing address.
   * If the billing address and beneficiaries address should be equal the values are being updated using updateBeneficiariesAddress()
   *
   * @param event Boolean of the checkbox that determines if the beneficiaries address and billing address should have the same values
   */
  toggleBeneficiaryAddress(event){
    this.shouldBillingAddressEqualBeneficiaryAddress = event.target.checked
    this.updateBeneficiaryAddress(this.shouldBillingAddressEqualBeneficiaryAddress);
  }

  /**
   * Determines if the billing address of the saved property form is equal to the beneficiaries address of the saved property form
   *
   * @return true if the billing address is equal to the beneficiaries address else false
   */
  isBillingAddressEqualsBeneficiaryAddress(): boolean {
    const beneficiariesAddress = this.savedProperty.beneficiariesAddress;
    const billingAddress = this.savedProperty.billingAddress;

    if (!beneficiariesAddress || !billingAddress) {
      return false;
    }

    const isBeneficiaryEqualBillingFirma = beneficiariesAddress.beneficiary === billingAddress.firma;
    const isTownEqual = beneficiariesAddress.town === billingAddress.town;
    const isZipEqual = beneficiariesAddress.zip === billingAddress.zip;
    const isStreetEqual = beneficiariesAddress.street === billingAddress.street;

    return isBeneficiaryEqualBillingFirma && isTownEqual && isZipEqual && isStreetEqual;
  }

  /**
   * If the saved property form has changed the user is asked if he really want to leave the page since unsaved data might be lost
   */
  @HostListener('window:beforeunload', ['$event'])
  canExit(): Observable<boolean> | Promise<boolean> | boolean {
    if(this.savedPropertyForm && this.savedPropertyForm.dirty && !this.canExitWithoutAsking){
      return confirm("Ihre Änderungen werden eventuell nicht gespeichert.")
    }
    else{
      return true;
    }
  }
}
